import { useEffect, useState } from 'react';
import { shiftOptions } from './receptionistData';
import PasswordField from '../../auth/PasswordField';
import '../../owner/ownerPages.css';
import '../../owner/subscriptions/subscriptions.css';
import './receptionists.css';

const emptyForm = {
  name: '',
  email: '',
  phone: '',
  branchId: '',
  branch: '',
  shift: shiftOptions[0],
  status: 'active',
  password: '',
  confirmPassword: '',
};

function ReceptionistForm({
  initialData,
  branchOptions = [],
  onSubmit,
  onCancel,
  submitLabel,
  isNew = false,
  needsLoginSetup = false,
}) {
  const normalizedBranches = branchOptions.map((opt) => (
    typeof opt === 'string' ? { id: '', name: opt } : opt
  ));

  const [form, setForm] = useState(() => {
    const initialBranchId = initialData?.branchId || '';
    const initialBranchName = initialData?.branch || '';
    const matched = (branchOptions || []).map((opt) => (
      typeof opt === 'string' ? { id: '', name: opt } : opt
    )).find(
      (b) => (initialBranchId && b.id === initialBranchId) || (initialBranchName && b.name === initialBranchName),
    ) || (branchOptions || []).map((opt) => (
      typeof opt === 'string' ? { id: '', name: opt } : opt
    ))[0];

    return {
      ...emptyForm,
      ...initialData,
      branchId: matched?.id || initialBranchId || '',
      branch: matched?.name || initialBranchName || '',
      password: '',
      confirmPassword: '',
    };
  });
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (form.branchId || form.branch || !branchOptions.length) return;
    const first = typeof branchOptions[0] === 'string'
      ? { id: '', name: branchOptions[0] }
      : branchOptions[0];
    setForm((prev) => ({
      ...prev,
      branchId: first.id || '',
      branch: first.name || '',
    }));
  }, [branchOptions, form.branchId, form.branch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'branchId') {
      const selected = branchOptions
        .map((opt) => (typeof opt === 'string' ? { id: '', name: opt } : opt))
        .find((b) => b.id === value || b.name === value);
      setForm((prev) => ({
        ...prev,
        branchId: selected?.id || value,
        branch: selected?.name || '',
      }));
      setFormError('');
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
    setFormError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!form.branchId && !form.branch) {
      setFormError('Please select a branch.');
      return;
    }

    if (form.password) {
      if (form.password.length < 6) {
        setFormError('Password must be at least 6 characters.');
        return;
      }
      if (form.password !== form.confirmPassword) {
        setFormError('Passwords do not match.');
        return;
      }
    } else if (!isNew && !needsLoginSetup && form.confirmPassword) {
      setFormError('Enter the new password to change login.');
      return;
    }

    onSubmit?.(form);
  };

  return (
    <form className="subscription-form" onSubmit={handleSubmit}>
      {formError && <p className="auth-error" role="alert">{formError}</p>}

      <div className="receptionist-form-grid">
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="name">Full Name</label>
          <input id="name" name="name" className="owner-form-input" value={form.name} onChange={handleChange} required />
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="email">Email (login)</label>
          <input id="email" name="email" type="email" className="owner-form-input" value={form.email} onChange={handleChange} required />
        </div>
        {isNew && (
          <>
            <div className="owner-form-group receptionist-form-full">
              <p className="receptionist-form-hint">
                Login password is optional. If Supabase blocks signup (rate limit), leave password empty,
                save the receptionist, then create the user in Supabase → Authentication → Users and link with SQL.
              </p>
            </div>
            <PasswordField
              id="password"
              name="password"
              label="Password (optional)"
              value={form.password}
              onChange={handleChange}
              autoComplete="new-password"
              minLength={6}
            />
            <PasswordField
              id="confirmPassword"
              name="confirmPassword"
              label="Confirm password"
              value={form.confirmPassword}
              onChange={handleChange}
              autoComplete="new-password"
            />
          </>
        )}
        {needsLoginSetup && !isNew && (
          <>
            <div className="owner-form-group receptionist-form-full">
              <p className="receptionist-form-hint">
                This receptionist has no login yet. Set a password here to enable sign-in,
                or create the user in Supabase → Authentication → Users (check Auto Confirm) and enter the same password below.
              </p>
            </div>
            <PasswordField
              id="password"
              name="password"
              label="Create login password"
              value={form.password}
              onChange={handleChange}
              autoComplete="new-password"
              minLength={6}
            />
            <PasswordField
              id="confirmPassword"
              name="confirmPassword"
              label="Confirm password"
              value={form.confirmPassword}
              onChange={handleChange}
              autoComplete="new-password"
            />
          </>
        )}
        {!isNew && !needsLoginSetup && (
          <>
            <div className="owner-form-group receptionist-form-full">
              <p className="receptionist-form-hint">
                Leave blank to keep the current login password. Fill both fields to change it.
              </p>
            </div>
            <PasswordField
              id="password"
              name="password"
              label="New password"
              value={form.password}
              onChange={handleChange}
              autoComplete="new-password"
              minLength={6}
            />
            <PasswordField
              id="confirmPassword"
              name="confirmPassword"
              label="Confirm new password"
              value={form.confirmPassword}
              onChange={handleChange}
              autoComplete="new-password"
            />
          </>
        )}
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="phone">Phone</label>
          <input id="phone" name="phone" type="tel" className="owner-form-input" value={form.phone} onChange={handleChange} required />
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="branchId">Branch</label>
          <select
            id="branchId"
            name="branchId"
            className="owner-form-select"
            value={form.branchId || form.branch || ''}
            onChange={handleChange}
            required
          >
            <option value="">Select branch...</option>
            {branchOptions.map((opt) => {
              const branch = typeof opt === 'string' ? { id: '', name: opt } : opt;
              return (
                <option key={branch.id || branch.name} value={branch.id || branch.name}>
                  {branch.name}
                </option>
              );
            })}
          </select>
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="shift">Shift</label>
          <select id="shift" name="shift" className="owner-form-select" value={form.shift} onChange={handleChange}>
            {shiftOptions.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="status">Status</label>
          <select id="status" name="status" className="owner-form-select" value={form.status} onChange={handleChange}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>
      <div className="subscription-form-actions">
        <button type="submit" className="owner-btn owner-btn-primary">{submitLabel}</button>
        {onCancel && (
          <button type="button" className="owner-btn owner-btn-secondary" onClick={onCancel}>Cancel</button>
        )}
      </div>
    </form>
  );
}

export default ReceptionistForm;
